import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
    ArrowLeft, 
    Heart,
    X,
    ChevronRight,
    ChevronDown,
    ChevronUp,
    Sparkles,
    Shield,
    Zap,
    Clock,
    Brain,
    Volume2,
    RefreshCw,
    Lightbulb,
    Users,
    MessageCircle
} from 'lucide-react';
import { IconButton, PrimaryButton, SecondaryButton } from '../components/brand/BrandButton';
import { BrandCard, SelectionCard } from '../components/brand/BrandCard';
import { Heading, BodyText, SmallText } from '../components/brand/BrandTypography';
import { IconContainer } from '../components/brand/BrandIcon';
import { Container } from '../components/brand/BrandLayout';

/**
 * ADHD-Specific Partner Support Module
 * 
 * Helps partners understand how ADHD shapes emotional experience
 * and how to respond in ways that create safety, connection, and calm.
 * 
 * Design principles:
 * - Soft, validating, non-blaming language
 * - Non-clinical, trauma-informed tone
 * - Optional and user-led
 * - ADHD-friendly: calming, spacious, digestible
 */

const ADHDPartnerSupport = () => {
    const navigate = useNavigate();
    const [step, setStep] = useState('intro');
    const [expandedAreas, setExpandedAreas] = useState({});

    // Support areas data
    const supportAreas = [
        {
            id: 'rejection-sensitivity',
            title: 'Rejection Sensitivity',
            icon: Heart,
            variant: 'rose',
            explanation: "ADHD can make the brain more sensitive to perceived rejection. Small cues can feel big.",
            guidance: [
                "Use warm tones when possible.",
                "Offer reassurance early, before the spiral starts.",
                "Say what you *do* feel, not what you don't."
            ],
            responses: [
                "I'm here.",
                "We're okay.",
                "I care about you."
            ]
        },
        {
            id: 'emotional-flooding',
            title: 'Emotional Flooding',
            icon: Zap,
            variant: 'blue',
            explanation: "ADHD emotions can go from 0 to 100 quickly. Flooding isn't intentional — it's a nervous system reaction.",
            guidance: [
                "Slow your pace and soften your voice.",
                "Give space without withdrawing connection.",
                "Offer grounding cues like 'Let's take a breath together.'"
            ],
            responses: [
                "Let's slow down.",
                "I'm with you."
            ]
        },
        {
            id: 'time-blindness',
            title: 'Time Blindness + Emotional Time Distortion',
            icon: Clock,
            variant: 'lavender',
            explanation: "During conflict, moments can feel longer, heavier, or more urgent.",
            guidance: [
                "Offer gentle time anchors.",
                "Reassure them that the moment will pass.",
                "Avoid phrases that imply permanence."
            ],
            responses: [
                "This is just a moment.",
                "We'll get through this together."
            ]
        },
        {
            id: 'working-memory',
            title: 'Working Memory Breakdowns',
            icon: Brain,
            variant: 'sage',
            explanation: "Stress makes it harder for ADHD brains to remember what they wanted to say or what helps.",
            guidance: [
                "Be patient with pauses.",
                "Repeat things gently without frustration.",
                "Offer simple choices instead of open-ended questions."
            ],
            responses: [
                "Take your time.",
                "We can slow down."
            ]
        },
        {
            id: 'sensory-overload',
            title: 'Sensory Overload',
            icon: Volume2,
            variant: 'blue',
            explanation: "Noise, tone, movement, or intensity can overwhelm the ADHD nervous system.",
            guidance: [
                "Lower sensory input when possible.",
                "Use a calm, steady tone.",
                "Reduce background noise or visual clutter."
            ],
            responses: [
                "Let's make this a little quieter.",
                "We can pause for a moment."
            ]
        },
        {
            id: 'shame-spirals',
            title: 'Shame Spirals',
            icon: Shield,
            variant: 'rose',
            explanation: "Many ADHDers carry long histories of being misunderstood. Shame can activate quickly.",
            guidance: [
                "Avoid criticism during dysregulation.",
                "Lead with reassurance.",
                "Name the good you see in them."
            ],
            responses: [
                "You're not in trouble.",
                "We're on the same team.",
                "You matter to me."
            ]
        },
        {
            id: 'transition-difficulties',
            title: 'Transition Difficulties',
            icon: RefreshCw,
            variant: 'lavender',
            explanation: "ADHD brains often need more time to shift states. Abrupt transitions can feel jarring.",
            guidance: [
                "Give gentle warnings before shifting topics.",
                "Avoid sudden emotional pivots.",
                "Use soft transitions like 'When you're ready…'"
            ],
            responses: [
                "Let's take this one step at a time."
            ]
        }
    ];

    // What helps most
    const whatHelpsMost = [
        "Slow your pace.",
        "Use warm, steady tones.",
        "Offer reassurance early.",
        "Validate the softer feeling underneath.",
        "Stay present without pushing for solutions.",
        "Reach for connection, not correction."
    ];

    const toggleArea = (areaId) => {
        setExpandedAreas(prev => ({
            ...prev,
            [areaId]: !prev[areaId]
        }));
    };

    const handleClose = () => {
        navigate('/relationship-hub');
    };

    const handleBack = () => {
        if (step === 'intro') {
            handleClose();
        } else if (step === 'areas') {
            setStep('intro');
        } else if (step === 'helps') {
            setStep('areas');
        } else if (step === 'reframe') {
            setStep('helps');
        }
    };

    const getVariantStyles = (variant) => {
        const styles = {
            rose: { bg: 'bg-[#FFBB98]/10', border: 'border-white/40/20', icon: 'text-[#B08080]' },
            blue: { bg: 'bg-[#7D8E95]/20', border: 'border-[#7D8E95]/30', icon: 'text-[#7D8E95]' },
            lavender: { bg: 'bg-[#7D8E95]/10', border: 'border-[#7D8E95]/20', icon: 'text-[#7D8E95]' },
            sage: { bg: 'bg-[#7D8E95]/10', border: 'border-[#7D8E95]/20', icon: 'text-[#1D6B6B]' }
        };
        return styles[variant] || styles.sage;
    };

    return (
        <div className="min-h-screen bg-transparent">
            {/* Header */}
            <header className="sticky top-0 z-50 glass-header">
                <Container size="sm" className="py-5 flex items-center justify-between">
                    <IconButton 
                        onClick={handleBack}
                        data-testid="back-btn"
                    >
                        <ArrowLeft className="w-5 h-5" />
                    </IconButton>
                    <Heading as="h1" size="text-lg">Partner Support</Heading>
                    <IconButton 
                        onClick={handleClose}
                        data-testid="close-btn"
                        className="hover:bg-[#FFBB98]/10"
                    >
                        <X className="w-5 h-5" />
                    </IconButton>
                </Container>
            </header>

            <main className="max-w-xl mx-auto px-6 py-8">
                {/* INTRO */}
                {step === 'intro' && (
                    <div className="brand-fade-in">
                        <div className="text-center mb-10">
                            <IconContainer variant="lavender" size="lg" useGradient={true} className="mx-auto mb-5">
                                <Users className="w-6 h-6" />
                            </IconContainer>
                            <Heading as="h2" size="text-2xl" className="mb-4">
                                ADHD-Specific Partner Support
                            </Heading>
                        </div>

                        <div className="glass-card rounded-2xl p-5">
                            <BodyText className="text-[#1D6B6B] leading-relaxed text-center text-lg">
                                ADHD can shape how emotions are felt, expressed, and interpreted. This module helps partners understand these patterns and respond in ways that feel soothing and supportive.
                            </BodyText>
                        </div>

                        <div className="space-y-3 mb-8">
                            <div className="flex items-start gap-3 p-4 rounded-xl bg-[#7D8E95]/10">
                                <Heart className="w-5 h-5 text-[#1D6B6B] flex-shrink-0 mt-0.5" />
                                <SmallText className="text-[#2A8585]">
                                    Learn how ADHD shapes emotional experience
                                </SmallText>
                            </div>
                            <div className="flex items-start gap-3 p-4 rounded-xl bg-[#7D8E95]/10">
                                <MessageCircle className="w-5 h-5 text-[#1D6B6B] flex-shrink-0 mt-0.5" />
                                <SmallText className="text-[#2A8585]">
                                    Discover soothing responses that help
                                </SmallText>
                            </div>
                            <div className="flex items-start gap-3 p-4 rounded-xl bg-[#7D8E95]/10">
                                <Shield className="w-5 h-5 text-[#1D6B6B] flex-shrink-0 mt-0.5" />
                                <SmallText className="text-[#2A8585]">
                                    Build safety, connection, and calm together
                                </SmallText>
                            </div>
                        </div>

                        <div className="space-y-4">
                            <PrimaryButton
                                onClick={() => setStep('areas')}
                                className="w-full"
                                size="xl"
                                data-testid="start-btn"
                            >
                                Begin
                                <ChevronRight className="w-4 h-4 ml-2" />
                            </PrimaryButton>
                        </div>

                        <div className="mt-10 text-center">
                            <SmallText className="text-[#7D8E95] italic">
                                This is optional. Go at your own pace.
                            </SmallText>
                        </div>
                    </div>
                )}

                {/* SUPPORT AREAS */}
                {step === 'areas' && (
                    <div className="brand-fade-in">
                        <div className="text-center mb-8">
                            <Heading as="h2" size="text-xl" className="mb-2">
                                Understanding ADHD Patterns
                            </Heading>
                            <BodyText className="text-[#2A8585]">
                                Tap any area to learn more
                            </BodyText>
                        </div>

                        <div className="space-y-4 mb-8">
                            {supportAreas.map((area, index) => {
                                const IconComponent = area.icon;
                                const styles = getVariantStyles(area.variant);
                                const isExpanded = expandedAreas[area.id];
                                
                                return (
                                    <div 
                                        key={area.id}
                                        className="brand-slide-up"
                                        style={{ animationDelay: `${index * 60}ms` }}
                                    >
                                        <div 
                                            className={`rounded-2xl border ${styles.border} overflow-hidden transition-all duration-300`}
                                        >
                                            {/* Header - clickable */}
                                            <button
                                                onClick={() => toggleArea(area.id)}
                                                className={`w-full p-4 ${styles.bg} flex items-center gap-4 text-left transition-colors hover:opacity-90`}
                                                data-testid={`area-${area.id}`}
                                            >
                                                <div className={`w-10 h-10 rounded-full ${styles.bg} border ${styles.border} flex items-center justify-center flex-shrink-0`}>
                                                    <IconComponent className={`w-5 h-5 ${styles.icon}`} />
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <SmallText className="text-[#1D6B6B] font-medium block">
                                                        {area.title}
                                                    </SmallText>
                                                </div>
                                                {isExpanded ? (
                                                    <ChevronUp className="w-5 h-5 text-[#7D8E95] flex-shrink-0" />
                                                ) : (
                                                    <ChevronDown className="w-5 h-5 text-[#7D8E95] flex-shrink-0" />
                                                )}
                                            </button>

                                            {/* Expanded content */}
                                            {isExpanded && (
                                                <div className="p-5 bg-white/50 border-t border-white/40/40 space-y-5">
                                                    {/* Explanation */}
                                                    <div>
                                                        <BodyText className="text-[#1D6B6B] leading-relaxed italic">
                                                            "{area.explanation}"
                                                        </BodyText>
                                                    </div>

                                                    {/* Partner Guidance */}
                                                    <div>
                                                        <SmallText className="text-[#7D8E95] font-medium mb-3 block">
                                                            Partner guidance:
                                                        </SmallText>
                                                        <ul className="space-y-2">
                                                            {area.guidance.map((tip, i) => (
                                                                <li key={i} className="flex items-start gap-2">
                                                                    <span className="text-[#1D6B6B] mt-1">•</span>
                                                                    <SmallText className="text-[#2A8585]">
                                                                        {tip}
                                                                    </SmallText>
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </div>

                                                    {/* Soothing Responses */}
                                                    <div className={`p-4 rounded-xl ${styles.bg} border ${styles.border}`}>
                                                        <SmallText className="text-[#7D8E95] font-medium mb-3 block">
                                                            Soothing responses:
                                                        </SmallText>
                                                        <div className="flex flex-wrap gap-2">
                                                            {area.responses.map((response, i) => (
                                                                <span 
                                                                    key={i}
                                                                    className="px-3 py-1.5 rounded-full bg-white/70 text-sm text-[#2A8585] italic"
                                                                >
                                                                    "{response}"
                                                                </span>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        <PrimaryButton
                            onClick={() => setStep('helps')}
                            className="w-full"
                            size="xl"
                        >
                            Continue
                            <ChevronRight className="w-4 h-4 ml-2" />
                        </PrimaryButton>
                    </div>
                )}

                {/* WHAT HELPS MOST */}
                {step === 'helps' && (
                    <div className="brand-fade-in">
                        <div className="text-center mb-8">
                            <IconContainer variant="sage" size="lg" useGradient={true} className="mx-auto mb-5">
                                <Lightbulb className="w-6 h-6" />
                            </IconContainer>
                            <Heading as="h2" size="text-2xl" className="mb-3">
                                What Helps Most
                            </Heading>
                            <BodyText className="text-[#2A8585]">
                                Simple, doable actions for partners
                            </BodyText>
                        </div>

                        <div className="space-y-3 mb-8">
                            {whatHelpsMost.map((action, index) => (
                                <div 
                                    key={index}
                                    className="brand-slide-up flex items-center gap-4 p-4 rounded-xl bg-[#7D8E95]/10 border border-[#7D8E95]/20"
                                    style={{ animationDelay: `${index * 80}ms` }}
                                >
                                    <div className="w-8 h-8 rounded-full bg-[#7D8E95]/30 flex items-center justify-center flex-shrink-0">
                                        <Heart className="w-4 h-4 text-[#1D6B6B]" />
                                    </div>
                                    <BodyText className="text-[#1D6B6B]">
                                        {action}
                                    </BodyText>
                                </div>
                            ))}
                        </div>

                        <div className="flex gap-4">
                            <SecondaryButton onClick={handleBack} className="flex-1" size="lg">
                                Back
                            </SecondaryButton>
                            <PrimaryButton 
                                onClick={() => setStep('reframe')} 
                                className="flex-1" 
                                size="lg"
                            >
                                Continue
                                <ChevronRight className="w-4 h-4 ml-2" />
                            </PrimaryButton>
                        </div>
                    </div>
                )}

                {/* HOPEFUL REFRAME */}
                {step === 'reframe' && (
                    <div className="brand-fade-in">
                        <div className="text-center mb-8">
                            <IconContainer variant="rose" size="lg" useGradient={true} className="mx-auto mb-5">
                                <Sparkles className="w-6 h-6" />
                            </IconContainer>
                        </div>

                        <div className="glass-card rounded-2xl p-5">
                            <BodyText className="text-[#1D6B6B] leading-relaxed text-center text-lg">
                                ADHD doesn't make relationships harder — it makes emotional needs clearer. When partners understand these patterns, they often find deeper closeness, safety, and connection.
                            </BodyText>
                        </div>

                        <div className="p-5 rounded-2xl bg-[#FFBB98]/10 border border-white/40/20 mb-8">
                            <BodyText className="text-[#2A8585] leading-relaxed text-center italic">
                                Understanding is the first step. Connection follows.
                            </BodyText>
                        </div>

                        <div className="space-y-4">
                            <PrimaryButton
                                onClick={handleClose}
                                className="w-full"
                                size="xl"
                                data-testid="done-btn"
                            >
                                <Heart className="w-4 h-4 mr-2" />
                                Return to Hub
                            </PrimaryButton>
                            <SecondaryButton
                                onClick={() => setStep('areas')}
                                className="w-full"
                                size="lg"
                            >
                                Review support areas
                            </SecondaryButton>
                        </div>

                        <div className="mt-10 text-center">
                            <SmallText className="text-[#7D8E95] italic">
                                You can return to this anytime.
                            </SmallText>
                        </div>

                        {/* Link to Partner Mode */}
                        <div className="mt-6 pt-6 border-t border-white/40/60">
                            <Link to="/regulate/partner-mode" className="block">
                                <div className="p-4 rounded-2xl bg-[#7D8E95]/10 hover:bg-[#7D8E95]/20 transition-colors border border-[#7D8E95]/20 group">
                                    <div className="flex items-center gap-3">
                                        <div className="w-8 h-8 rounded-full bg-[#7D8E95]/30 flex items-center justify-center flex-shrink-0">
                                            <Users className="w-4 h-4 text-[#7D8E95]" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <SmallText className="text-[#2A8585] font-medium block mb-0.5">
                                                Ready to practice together?
                                            </SmallText>
                                            <SmallText className="text-[#7D8E95] group-hover:text-[#7D8E95] transition-colors">
                                                Try Partner Mode for real-time support
                                            </SmallText>
                                        </div>
                                        <ChevronRight className="w-4 h-4 text-[#7D8E95] group-hover:text-[#7D8E95] transition-colors" />
                                    </div>
                                </div>
                            </Link>
                        </div>
                    </div>
                )}
            </main>
        </div>
    );
};

export default ADHDPartnerSupport;
